import React, { useState, useRef } from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  FormGroup,
  Button,
  Checkbox,
  RadioGroup,
  FormControlLabel,
  Radio,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  CircularProgress,
  Paper,
  Switch,
  SwitchProps,
  styled,
  useTheme
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DateTimePickerWithTimezone from 'components/DateTimePickerWithTimezone';
import { Camera } from 'react-camera-pro';
import MultiFileUploader from './fileUpload';
import useApi from 'hooks/useApi';
import { InputComponents } from 'types/auth';
import ResourceDetailsDialog from 'components/ResourceRating/ResourceDetailsDialog';
import JumioVerificationBadge from 'components/JumioVerificationBadge';
import useJumioVerifications from 'hooks/useJumioVerifications';
import { resolveDynamicOptions } from 'utils/dynamicFieldOptions';


const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  display: 'flex',
  '&:active .MuiSwitch-thumb': {
    width: 22,
  },
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#65C466' : '#65C466',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 22,
    height: 22,
    borderRadius: 11,
    transition: theme.transitions.create(['width'], {
      duration: 200,
    }),
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    opacity: 1,
    backgroundColor:
      theme.palette.mode === 'dark' ? '#39393D' : '#E9E9EA',
    boxSizing: 'border-box',
  },
}));

interface EditFormProps {
  stepItem: any;
  onSave: (stepId: string, updatedData: any) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
  transactionHistory?: any[];
}

// Dynamic field option resolution lives in utils/dynamicFieldOptions so this
// form and formData.tsx cannot drift apart. See resolveDynamicOptions there.

interface Field {
  field: any;
  description: any;
  question: string;
  inputComponent: InputComponents;
  hasAddIcon?: boolean;
  options?: string[];
  resourceType?: string;
  resourceList?: string[];
  dropdownSource?: string;
  settings?: {
    noOfImages?: number;
    required?: boolean;
    multiple?: boolean;
    minDuration?: number;
    min?: number;
    resourceType?: string;
    dropdownSource?: string;
    isTimeRequired?: boolean;
    isMultiline?: boolean;
    rows?: number;
    [key: string]: any;
  };
  uid?: string;
}

// Label for a resource in the picker. `fields` is keyed by the resource-type's
// display labels, not camelCase, so name-ish labels are matched on letters alone.
// Only labels that unambiguously hold a whole name short-circuit: a plain `Name`
// label does not, because types commonly pair it with a separate `Last Name`,
// and returning on `Name` alone is what used to drop the surname here.
const getResourceDisplayName = (resource: any): string => {
  if (!resource?.fields || typeof resource.fields !== 'object') {
    return resource?.name || `Resource ${resource?.id || resource?._id || 'Unknown'}`;
  }
  const f = resource.fields;

  const norm = (k: string) => k.toLowerCase().replace(/[^a-z]/g, '');
  const pick = (...names: string[]): string => {
    const target = names.map(norm);
    for (const [key, val] of Object.entries(f)) {
      if (target.includes(norm(key)) && (typeof val === 'string' || typeof val === 'number') && String(val).trim()) {
        return String(val).trim();
      }
    }
    return '';
  };

  const full = pick('fullName', 'displayName');
  if (full) return full;

  const first = pick('firstName', 'first', 'givenName', 'name');
  const last = pick('lastName', 'last', 'secondName', 'surname', 'familyName');
  if (first || last) {
    return first && last && first !== last ? `${first} ${last}` : (first || last);
  }

  const fieldValues = Object.values(f);
  return fieldValues.length > 0 ? String(fieldValues[0]) : 'Unnamed Resource';
};

// ResourceSelector Component for handling RESOURCE input type
interface ResourceSelectorProps {
  field: Field;
  value: any;
  onChange: (value: any) => void;
  resourceType?: string;
  resourceList?: string[];
  multiple?: boolean;
}

const ResourceSelector: React.FC<ResourceSelectorProps> = ({ field, value, onChange, resourceType, resourceList, multiple = false }) => {
  const { searchResources, getResourceTypeById, createResourceData, showToast, user } = useApi();
  
  // Country codes for dropdown in add resource dialog
  const countryCodes = [
    '1', '7', '20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49', '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84', '86', '87', '087', '90', '91', '92', '93', '94', '95', '98', '212', '213', '216', '218', '220', '221', '222', '223', '224', '225', '226', '227', '228', '229', '230', '231', '232', '233', '234', '235', '236', '237', '238', '239', '240', '241', '242', '243', '244', '245', '246', '248', '249', '250', '251', '252', '253', '254', '255', '256', '257', '258', '260', '261', '262', '263', '264', '265', '266', '267', '268', '269', '290', '291', '297', '298', '299', '350', '351', '352', '353', '354', '355', '356', '357', '358', '359', '370', '371', '372', '373', '374', '375', '376', '377', '378', '380', '381', '382', '383', '385', '386', '387', '389', '420', '421', '423', '500', '501', '502', '503', '504', '505', '506', '507', '508', '509', '590', '591', '592', '593', '594', '595', '596', '597', '598', '599', '670', '672', '673', '674', '675', '676', '677', '678', '679', '680', '681', '682', '683', '684', '685', '686', '687', '688', '689', '690', '691', '692', '850', '852', '853', '855', '856', '880', '886', '960', '961', '962', '963', '964', '965', '966', '967', '968', '970', '971', '972', '973', '974', '975', '976', '977', '992', '993', '994', '995', '996', '998'
  ];


  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingResource, setViewingResource] = useState<any>(null);

  // Function to handle view dialog close and reset tab
  const handleViewDialogClose = () => {
    setShowViewDialog(false);
  };
  const [addFormFields, setAddFormFields] = useState<any[]>([]);
  const [addFormValues, setAddFormValues] = useState<any>({});
  const [addingResource, setAddingResource] = useState(false);
  const [selectedResources, setSelectedResources] = useState<any[]>([]);

  // Jumio state for everyone this picker can show, so a driver's verification is
  // visible at the moment of assignment.
  const verificationOf = useJumioVerifications(
    React.useMemo(() => [...selectedResources, ...searchResults], [selectedResources, searchResults]),
  );

  // Update selectedResources when value prop changes
  React.useEffect(() => {
    if (Array.isArray(value) && multiple) {
      setSelectedResources(value.filter(v => v && (v.id || v._id)));
    } else if (value && !multiple && (value.id || value._id)) {
      setSelectedResources([value]);
    } else {
      setSelectedResources([]);
    }
  }, [value, multiple]);

  // Debounced search effect
  React.useEffect(() => {
    setSearchResults([]);

    if (searchTerm.trim() === '') {
      return;
    }

    const hasAnyType = !!resourceType || (resourceList && resourceList.length > 0);
    const delayedSearch = setTimeout(() => {
      if (searchTerm && hasAnyType) {
        handleSearch();
      }
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, resourceType, resourceList]);

  const handleSearch = async () => {
    // The Search column (resourceList) is the authoritative source for what's searchable.
    // Primary (resourceType) is only the auto-update target — NOT a search source.
    // Backward-compat: fall back to [resourceType] only when resourceList is unset.
    const effectiveTypes: string[] = (resourceList && resourceList.length > 0)
      ? resourceList
      : (resourceType ? [resourceType] : []);

    if (effectiveTypes.length === 0) {
      setSearchResults([]);
      return;
    }
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);

    try {
      const response = await searchResources(null, searchTerm.trim(), effectiveTypes);

      if (response?.success && Array.isArray(response.data?.data)) {
        const permanentResources = response.data.data || [];
        setSearchResults(permanentResources);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Resource search failed:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectResource = (resource: any) => {
    if (!resource || (!resource.id && !resource._id)) {
      console.warn('Cannot select resource without valid ID:', resource);
      return;
    }

    const resourceId = resource.id || resource._id;
    
    // Ensure resourceType, category, and allowRating are attached to the resource object
    const resourceWithType = {
      ...resource,
      category: resource.category || resource.resourceTypeId?.category || resource.resourceType?.category || false,
    };

    if (multiple) {
      const isSelected = selectedResources.some((r) => (r.id || r._id) === resourceId);
      let newSelection;

      if (isSelected) {
        newSelection = selectedResources.filter((r) => (r.id || r._id) !== resourceId);
      } else {
        newSelection = [...selectedResources, resourceWithType];
      }

      setSelectedResources(newSelection);
      onChange(newSelection);
    } else {
      setSelectedResources([resourceWithType]);
      onChange(resourceWithType);
    }
    
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleRemoveResource = (resourceId: string) => {
    if (!resourceId) {
      console.warn('Cannot remove resource without valid ID');
      return;
    }

    const newSelection = selectedResources.filter((r) => (r.id || r._id) !== resourceId);
    setSelectedResources(newSelection);
    onChange(multiple ? newSelection : null);
  };

  const handleViewResource = (resource: any) => {
    setViewingResource(resource);
    setShowViewDialog(true);
  };

  const handleAddResource = async () => {
    if (!resourceType) {
      console.error('No resource type specified');
      return;
    }

    try {
      const response = await getResourceTypeById(resourceType);
      if (response?.success && response.data?.fields) {
        // Filter out fields with dropdownSource: "teams" to prevent them from being displayed
        const filteredFields = response.data.fields.filter((field: any) => 
          field.dropdownSource !== 'teams'
        );
        
        setAddFormFields(filteredFields);
        // Initialize form values only for filtered fields
        const initialValues: any = {};
        filteredFields.forEach((field: any) => {
          initialValues[field.name] = '';
        });
        setAddFormValues(initialValues);
        setShowAddDialog(true);
      } else {
        console.error('Failed to get resource type fields');
      }
    } catch (error) {
      console.error('Error fetching resource type fields:', error);
    }
  };

  const handleSaveResource = async () => {
    if (!resourceType) {
      console.error('No resource type specified');
      return;
    }

    setAddingResource(true);

    try {
      // Call the API to create the resource
      const response = await createResourceData(resourceType, addFormValues);
      
      if (response?.success) {
        const newResource = {
          ...response.data,
          resourceType: response.data.resourceType || resourceType
        };
        showToast('Resource created successfully', 'success');

        // Add the newly created resource to the selection
        if (multiple) {
          const newSelection = [...selectedResources, newResource];
          setSelectedResources(newSelection);
          onChange(newSelection);
        } else {
          setSelectedResources([newResource]);
          onChange(newResource);
        }

        setShowAddDialog(false);
        setAddFormValues({});
        setSearchTerm('');
      } else {
        console.error('Failed to create resource:', response?.message);
        showToast(response?.message || 'Failed to create resource', 'error');
      }
    } catch (error: any) {
      console.error('Error creating resource:', error);
      showToast(error?.message || 'Failed to create resource', 'error');
    } finally {
      setAddingResource(false);
    }
  };

  const renderSearchResultItem = (resource: any) => {
    const displayName = getResourceDisplayName(resource);

    return (
      <Box
        key={resource.id || resource._id}
        sx={{
          py: "5px",
          px: "5px",
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:last-child': {
            borderBottom: 'none'
          },
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        <Box flex={1} display="flex" alignItems="center" gap={1} minWidth={0}>
          <Typography
            variant="body2"
            title={displayName}
            sx={{ flex: 1, minWidth: 0, fontSize: '0.875rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {displayName}
          </Typography>
          <JumioVerificationBadge verification={verificationOf(resource)} />
          {resource.resourceTypeId?.name && (
            <Typography
              variant="caption"
              title={resource.resourceTypeId.name}
              sx={{
                flexShrink: 1,
                minWidth: 0,
                maxWidth: '40%',
                px: 0.75,
                py: 0.25,
                fontSize: '0.7rem',
                borderRadius: 0.75,
                backgroundColor: 'action.selected',
                color: 'text.secondary',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {resource.resourceTypeId.name}
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={0.5}>
          <IconButton size="small" onClick={() => handleViewResource(resource)} sx={{ minWidth: 'auto', p: 0.5 }}>
            <VisibilityIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleSelectResource(resource)}
            sx={{ minWidth: 'auto', px: 1.5, py: 0.25, fontSize: '0.75rem' }}
          >
            Select
          </Button>
        </Box>
      </Box>
    );
  };

  const renderSelectedResource = (resource: any) => {
    const displayName = getResourceDisplayName(resource);

    return (
      <Box
        key={resource.id || resource._id}
        sx={{
          py: 0.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant="body2" sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {displayName}
          <JumioVerificationBadge verification={verificationOf(resource)} />
        </Typography>
        <Box display="flex" gap={0.5}>
          <IconButton size="small" onClick={() => handleViewResource(resource)} sx={{ p: 0.5 }}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleRemoveResource(resource.id || resource._id)} sx={{ p: 0.5 }}>
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {/* Selected Resources Display */}
      {selectedResources.length > 0 && (
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Selected {multiple ? 'Resources' : 'Resource'}:
          </Typography>
          {selectedResources.map((resource) => renderSelectedResource(resource))}
        </Box>
      )}

      {/* Search Input */}
      <TextField
        fullWidth
        placeholder="Search resources..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {loading && <CircularProgress size={20} />}
              <SearchIcon />
            </InputAdornment>
          )
        }}
        sx={{ mb: 2 }}
      />

      {/* Search Results */}
      {searchTerm && (
        <Box>
          {searchResults.length > 0 ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Search Results:
              </Typography>
              <Box
                maxHeight={250}
                overflow="auto"
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 2
                }}
              >
                {searchResults
                  .filter((resource) => !selectedResources.some((selected) => selected.id === resource.id))
                  .map((resource) => renderSearchResultItem(resource))}
              </Box>
            </Box>
          ) : (
            !loading && (
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ py: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No results found
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddResource}
                  sx={{ minWidth: 'auto', px: 2 }}
                >
                  Add
                </Button>
              </Box>
            )
          )}
        </Box>
      )}

      {/* Add Resource Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Resource</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {addFormFields.map((field: any, index: number) => (
              <Grid item xs={12} key={index}>
                {/* Check if this is a dropdown field with country code source */}
                {field.type === 'dropdown' && (field.dropdownSource === 'countryCode' || field.settings?.dropdownSource === 'countryCode') ? (
                  <TextField
                    fullWidth
                    select
                    label={field.name}
                    required={field.required}
                    value={addFormValues[field.name] || ''}
                    onChange={(e) => setAddFormValues((prev: any) => ({
                      ...prev,
                      [field.name]: e.target.value
                    }))}
                  >
                    <MenuItem value="">Please select</MenuItem>
                    {countryCodes.map((code: string) => (
                      <MenuItem key={code} value={code}>
                        {code}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  /* Default field rendering */
                  <TextField
                    fullWidth
                    label={field.name}
                    required={field.required}
                    type={field.type === 'number' ? 'number' : 'text'}
                    multiline={field.type === 'textarea'}
                    rows={field.type === 'textarea' ? 3 : 1}
                    value={addFormValues[field.name] || ''}
                    onChange={(e) => setAddFormValues((prev: any) => ({
                      ...prev,
                      [field.name]: e.target.value
                    }))}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveResource}
            disabled={addingResource || addFormFields.some((field: any) => field.required && !addFormValues[field.name]?.trim())}
          >
            {addingResource ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Resource Dialog */}
      <ResourceDetailsDialog
        open={showViewDialog}
        onClose={handleViewDialogClose}
        resource={viewingResource}
        allowRating={false}
        userId={user?.uid || user?.id || ''}
      />
    </Box>
  );
};

const EditForm: React.FC<EditFormProps> = ({ stepItem, onSave, onCancel, saving, transactionHistory }) => {
  const { uploadAsset } = useApi();
  // State for UI interactions
  const [passwordVisibility, setPasswordVisibility] = useState<Record<string, boolean>>({});
  const [cameraField, setCameraField] = useState<string>('');
  const cameraRef = useRef<any>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const theme = useTheme(); 

  const handleTogglePassword = (key: string) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Early return if stepItem is invalid
  if (!stepItem) {
    return (
      <Paper sx={{ 
        p: 3, 
        textAlign: 'center',
        backgroundColor: '#fafafa',
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Typography variant="body1" color="error">
          Error: No step data available for editing.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={onCancel}
          sx={{ mt: 2 }}
        >
          Close
        </Button>
      </Paper>
    );
  }

  // Get schema fields from stepItem.step (like formData.tsx does)
  const getSchemaFields = (): Field[] => {
    if (!stepItem?.step?.component?.fields) {
      return [];
    }

    const schemaFields = stepItem.step.component.fields;

    try {
      const fields = schemaFields.map((schemaField: any, index: number) => {
        if (!schemaField) {
          console.warn(`Found null/undefined schema field at index ${index}`);
          return null;
        }
        
        return {
          field: schemaField.field,
          description: schemaField.description,
          question: schemaField.question,
          inputComponent: schemaField.inputComponent as InputComponents,
          hasAddIcon: schemaField.hasAddIcon,
          options: schemaField.settings?.isDynamicField
            ? resolveDynamicOptions(
                schemaField.settings,
                transactionHistory || [],
                (stepItem?.data || []).find((d: any) => d?.question === schemaField.question)?.value?.value
              )
            : Array.isArray(schemaField.options)
            ? schemaField.options
            : [],
          resourceType: schemaField.resourceType,
          resourceList: schemaField.resourceList,
          dropdownSource: schemaField.dropdownSource,
          settings: schemaField.settings || {},
          uid: schemaField.uid
        };
      }).filter(Boolean);
      
      return fields;
    } catch (error) {
      console.error('Error in getSchemaFields:', error);
      return [];
    }
  };

  // Patch event values into schema fields
  const patchEventValuesIntoSchema = (schemaFields: Field[], eventData: any[]): Record<string, any> => {
    const patchedValues: Record<string, any> = {};
    
    // Create a lookup map for schema fields by question for easy access
    const schemaFieldMap = new Map<string, Field>();
    schemaFields.forEach((field) => {
      schemaFieldMap.set(field.question, field);
    });
    
    // Initialize all schema fields with default values
    schemaFields.forEach((field) => {
      switch (field.inputComponent) {
        case InputComponents.CHECKBOX:
          patchedValues[field.question] = [];
          break;
        case InputComponents.CAMERA:
          patchedValues[field.question] = [];
          break;
        case InputComponents.RESOURCE:
          patchedValues[field.question] = field.settings?.multiple ? [] : null;
          break;
        case InputComponents.FILE || InputComponents.ESIGN:
          patchedValues[field.question] = [];
          break;
        case InputComponents.SWITCH:
          patchedValues[field.question] = { value: false };
          break;
        case InputComponents.DATE:
          patchedValues[field.question] = null;
          break;
        case InputComponents.TEXT:
          // Initialize TEXT fields properly based on multiple setting
          patchedValues[field.question] = field.settings?.multiple ? [] : '';
          break;
        default:
          patchedValues[field.question] = '';
      }
    });

    // Patch in actual event values if they exist
    if (Array.isArray(eventData)) {
      eventData.forEach((dataField: any) => {
        if (dataField && dataField.question) {
          const fieldValue = dataField.value?.value;
          const schemaField = schemaFieldMap.get(dataField.question);
          // Handle different field types for patching
          switch (dataField.inputComponent) {

            case InputComponents.TEXT:
              const isMultiple = dataField.settings?.multiple || schemaField?.settings?.multiple;
              // A counter field is a comma-separated list typed into one box (its
              // hint says so, and the detail view counts it by splitting on
              // /[\s,]+/). Its stored value can carry several entries — anything
              // created through the integration API does — so taking [0] both hid
              // the rest here and, on save, wrote them away. Join instead.
              const isCounter = dataField.settings?.isCounter || schemaField?.settings?.isCounter;
              if (isMultiple && Array.isArray(fieldValue)) {
                patchedValues[dataField.question] = fieldValue;
              } else if (isCounter && Array.isArray(fieldValue)) {
                patchedValues[dataField.question] = fieldValue.filter(Boolean).join(', ');
              } else {
                patchedValues[dataField.question] = Array.isArray(fieldValue) ? fieldValue[0] || '' : fieldValue || '';
              }
              break;
              
            case InputComponents.RESOURCE:
              if (fieldValue) {
                patchedValues[dataField.question] = fieldValue;
              }
              break;
              
            case InputComponents.DATE:
              console.log(`Processing DATE field "${dataField.question}":`, { 
                fieldValue, 
                isArray: Array.isArray(fieldValue),
                arrayLength: Array.isArray(fieldValue) ? fieldValue.length : 'N/A'
              });
              
              if (fieldValue) {
                try {
                  // Handle array format (which is how we save it)
                  let dateVal = fieldValue;
                  if (Array.isArray(fieldValue) && fieldValue.length > 0) {
                    dateVal = fieldValue[0];
                    console.log(`Extracted from array for "${dataField.question}":`, dateVal);
                  }
                  
                  if (dateVal) {
                    // Check if it's the new format with timezone
                    if (typeof dateVal === 'object' && dateVal !== null && 'date' in dateVal && 'timezone' in dateVal) {
                      // New format with timezone - keep the full object
                      console.log(`Found timezone format for "${dataField.question}":`, dateVal);
                      patchedValues[dataField.question] = dateVal;
                    } else {
                      // Legacy format - it's a date string or Date object
                      // Just pass it through, DateTimePickerWithTimezone can handle it
                      console.log(`Legacy date format for "${dataField.question}":`, dateVal);
                      patchedValues[dataField.question] = dateVal;
                    }
                  }
                } catch (error) {
                  console.warn(`Error parsing date for field "${dataField.question}":`, error);
                  patchedValues[dataField.question] = null;
                }
              }
              break;
              
            case InputComponents.CHECKBOX:
              if (Array.isArray(fieldValue)) {
                patchedValues[dataField.question] = fieldValue;
              } else {
                patchedValues[dataField.question] = [];
              }
              break;
              
            case InputComponents.CAMERA:
              patchedValues[dataField.question] = dataField.value?.files || [];
              break;
              
            case InputComponents.FILE :
              patchedValues[dataField.question] = dataField.value?.files || [];
              break;

            case InputComponents.ESIGN:
              patchedValues[dataField.question] = dataField.value?.files || [];
              break;

            case InputComponents.SWITCH:
              
              let switchValue = false;
              const possibleValues = [
                fieldValue, // dataField.value?.value
                dataField.value, // dataField.value directly
                dataField.value?.checked, // dataField.value?.checked
                dataField.checked // dataField.checked directly
              ];
              
              
              for (const val of possibleValues) {
                if (val !== undefined && val !== null) {
                  if (typeof val === 'boolean') {
                    switchValue = val;
                    break;
                  } else if (Array.isArray(val) && val.length > 0) {
                    // Handle array values like [true] or [false]
                    const firstVal = val[0];
                    if (typeof firstVal === 'boolean') {
                      switchValue = firstVal;
                      break;
                    } else if (typeof firstVal === 'string') {
                      if (firstVal === 'true') {
                        switchValue = true;
                        break;
                      } else if (firstVal === 'false') {
                        switchValue = false;
                        break;
                      }
                    } else if (firstVal === 1 || firstVal === '1') {
                      switchValue = true;
                      break;
                    } else if (firstVal === 0 || firstVal === '0') {
                      switchValue = false;
                      break;
                    }
                  } else if (typeof val === 'string') {
                    if (val === 'true') {
                      switchValue = true;
                      break;
                    } else if (val === 'false') {
                      switchValue = false;
                      break;
                    }
                  } else if (val === 1 || val === '1') {
                    switchValue = true;
                    break;
                  } else if (val === 0 || val === '0') {
                    switchValue = false;
                    break;
                  }
                }
              } 
              patchedValues[dataField.question] = { value: switchValue };
              break;
              
            case InputComponents.SELECT:
            case InputComponents.RADIO:
              // Handle SELECT and RADIO - extract string value from array if needed
              if (Array.isArray(fieldValue)) {
                patchedValues[dataField.question] = fieldValue.length > 0 ? fieldValue[0] : '';
              } else {
                patchedValues[dataField.question] = fieldValue || '';
              }
              break;
              
            default:
              patchedValues[dataField.question] = fieldValue || '';
          }
        }
      });
    }

    return patchedValues;
  };

  const schemaFields = getSchemaFields();
  

  // If we couldn't extract any fields, show an error
  if (!schemaFields || schemaFields.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          background: 'white',
          borderRadius: "10px",
          marginBottom: "10px",
          border: '1px solid',
          borderColor: 'grey.200',
          transition: 'all 0.2s ease-in-out',
          overflow: 'hidden'
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            border: 'none',
            outline: 'none',
            color: '#fff',
            px: 2,
            py: 1.5,
            backgroundColor: '#132D76',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography
            fontWeight={600}
            variant="h6"
            sx={{
              color: '#fff',
              fontSize: '14px',
              letterSpacing: '0.025em',
            }}
          >
            Edit Error
          </Typography>
        </Box>
        
        {/* Content Section */}
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="error" sx={{ mb: 1 }}>
            Error: No editable fields found in this step schema.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The step may not have valid fields to be edited.
          </Typography>
          
          {/* Footer Section with button */}
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'grey.200',
              mt: 3,
              pt: 2,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={onCancel}
              sx={{
                borderColor: 'grey.300',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'grey.400',
                  bgcolor: 'grey.50'
                }
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Paper>
    );
  }

  // Get initial values by patching event data into schema
  const getInitialValues = (): Record<string, any> => {
    const initialValues = patchEventValuesIntoSchema(schemaFields, stepItem.data);

    console.log('EditForm - stepItem.data:', stepItem.data);
    console.log('EditForm - Initial values after patching:', initialValues);
    
    return initialValues;
  };

  // Validation schema using schema fields (like formData.tsx)
  const isEmptyDateValue = (value: any) =>
    value == null || value === '' || (Array.isArray(value) && value.length === 0);

  const isValidDateValue = (value: any) => {
    const normalizedValue = Array.isArray(value) ? value[0] : value;

    if (isEmptyDateValue(normalizedValue)) {
      return true;
    }

    if (typeof normalizedValue === 'object' && normalizedValue !== null && 'date' in normalizedValue) {
      const date = new Date(normalizedValue.date);
      return !isNaN(date.getTime()) && date >= new Date(1900, 0, 1);
    }

    if (normalizedValue instanceof Date) {
      return !isNaN(normalizedValue.getTime()) && normalizedValue >= new Date(1900, 0, 1);
    }

    if (typeof normalizedValue === 'string') {
      const date = new Date(normalizedValue);
      return !isNaN(date.getTime()) && date >= new Date(1900, 0, 1);
    }

    return false;
  };

  const validationSchema = Yup.object(
    schemaFields?.reduce((schema: any, field: any) => {
      if (field.inputComponent === InputComponents.DATE) {
        schema[field.question] = Yup.mixed()
          .nullable()
          .notRequired()
          .test('is-valid-date', 'this field must be a valid date', isValidDateValue);
      }
      if (field.settings?.required) {
        if (field.inputComponent === InputComponents.DATE) {
          schema[field.question] = Yup.mixed()
            .required(`${field.question} is required`)
            .test('is-valid-date', `${field.question} must be a valid date`, (value: any) => {
              if (!value) return false; // required, so null/undefined is invalid
              
              // Handle new format with timezone
              if (typeof value === 'object' && value !== null && 'date' in value) {
                const date = new Date(value.date);
                return !isNaN(date.getTime()) && date >= new Date(1900, 0, 1);
              }
              
              // Handle legacy format
              if (value instanceof Date) {
                return !isNaN(value.getTime()) && value >= new Date(1900, 0, 1);
              }
              
              // Try parsing as string
              if (typeof value === 'string') {
                const date = new Date(value);
                return !isNaN(date.getTime()) && date >= new Date(1900, 0, 1);
              }
              
              return false;
            });
        } else if (field.inputComponent === InputComponents.CHECKBOX) {
          schema[field.question] = Yup.array().min(1, `${field.question} is required`).required(`${field.question} is required`);
        } else if (field.inputComponent === InputComponents.CAMERA) {
          schema[field.question] = Yup.array().min(1, `${field.question} is required`).required(`${field.question} is required`);
        } else if (field.inputComponent === InputComponents.RESOURCE) {
          if (field.settings?.multiple) {
            schema[field.question] = Yup.array()
              .min(1, `${field.question} is required`)
              .required(`${field.question} is required`);
          } else {
            schema[field.question] = Yup.mixed()
              .test(
                'has-resource',
                `${field.question} is required`,
                (val: any) => val && typeof val === 'object' && !!(val.id || val._id)
              )
              .required(`${field.question} is required`);
          }
        } else {
          schema[field.question] = Yup.string().required(`${field.question} is required`);
        }
      }
      return schema;
    }, {})
  );

  // Transform form data for saving (like formData.tsx FormdataBinding)
  const transformFormDataForSave = (values: Record<string, any>) => {
    return schemaFields.map((field) => {
      const fieldValue = values[field.question];
      
      if (field.inputComponent === InputComponents.FILE) {
        return {
          field: field.uid,
          question: field.question,
          description: field.description ?? null,
          value: {
            value: '',
            files: Array.isArray(fieldValue) ? fieldValue : []
          },
          inputComponent: field.inputComponent,
          children: []
        };
      }

      if (field.inputComponent === InputComponents.RESOURCE) {
        let serializedValue: any = null;

        if (fieldValue) {
          if (Array.isArray(fieldValue)) {
            serializedValue = fieldValue.map((resource: any) => {
              if (!resource) return null;
              const resourceTypeToUse = resource.resourceType || field.resourceType || field.settings?.resourceType;
              if (!resourceTypeToUse) {
                console.warn('No resourceType found for resource:', resource);
              }
              return {
                id: resource.id || resource._id,
                fields: resource.fields || {},
                category: resource.category || resource.resourceType?.category || resource.resourceTypeId?.category,
              };
            }).filter(Boolean); // Remove any null entries
          } else {
            const resourceTypeToUse = fieldValue.resourceType || field.resourceType || field.settings?.resourceType;
            if (!resourceTypeToUse) {
              console.warn('No resourceType found for resource:', fieldValue);
            }
            serializedValue = {
              id: fieldValue.id || fieldValue._id,
              fields: fieldValue.fields || {},
              category: fieldValue.category || fieldValue.resourceTypeId?.category || fieldValue.resourceType?.category,
            };
          }
        }

        return {
          field: field.uid,
          question: field.question,
          description: field.description ?? null,
          value: {
            value: serializedValue,
            files: []
          },
          inputComponent: field.inputComponent,
          children: []
        };
      }

      if (field.inputComponent === InputComponents.TEXT) {
        let valueToSave = fieldValue;
        if (field.settings?.multiple) {
          valueToSave = Array.isArray(fieldValue) ? fieldValue : fieldValue ? [fieldValue] : [];
        } else if (field.settings?.isCounter) {
          // Mirror of the load above: the single box holds the whole
          // comma-separated list, so split it back out. Collapsing to [0] here is
          // what silently truncated these lists — the payload rewrites every
          // field in the step, so saving an unrelated change was enough to do it.
          valueToSave = (Array.isArray(fieldValue) ? fieldValue : [fieldValue])
            .flatMap((entry: any) => String(entry ?? '').split(','))
            .map((entry: string) => entry.trim())
            .filter(Boolean);
        } else {
          valueToSave = Array.isArray(fieldValue) ? [fieldValue[0] || ''] : fieldValue ? [fieldValue] : [];
        }
        
        return {
          field: field.uid,
          question: field.question,
          description: field.description ?? null,
          value: {
            value: valueToSave,
            files: []
          },
          inputComponent: field.inputComponent,
          children: []
        };
      }

      if (field.inputComponent === InputComponents.SWITCH) {
        // Extract the boolean value properly
        const booleanValue = fieldValue?.value ?? false;
        return {
          field: field.uid,
          question: field.question,
          description: field.description ?? null,
          value: {
            value: booleanValue,
            files: []
          },
          inputComponent: field.inputComponent,
          children: []
        };
      }

      if (field.inputComponent === InputComponents.DATE) {
        let dateValue = null;
        if (fieldValue) {
          // Check if it's the new format with timezone
          if (typeof fieldValue === 'object' && fieldValue !== null && 'date' in fieldValue && 'timezone' in fieldValue) {
            dateValue = fieldValue; // Keep the full object with timezone
          } else {
            // It's either a date string, Date object, or dayjs object
            // Convert it to the format expected by the backend
            try {
              let parsedDate;
              
              if (dayjs.isDayjs(fieldValue)) {
                parsedDate = fieldValue.toDate();
              } else if (fieldValue instanceof Date) {
                parsedDate = fieldValue;
              } else if (typeof fieldValue === 'string') {
                parsedDate = new Date(fieldValue);
              } else {
                parsedDate = fieldValue;
              }
              
              // Create date with timezone object
              if (parsedDate && !isNaN(parsedDate.getTime())) {
                const isTimeRequired = field.settings?.isTimeRequired || false;
                const includeTimezone = field.settings?.includeTimezone || false;
                
                // Get timezone (try to use browser timezone)
                const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
                
                dateValue = {
                  date: parsedDate.toISOString(),
                  timezone: timezone,
                  includeTime: isTimeRequired,
                  includeTimezone: includeTimezone
                };
              }
            } catch (error) {
              console.warn(`Error converting date for field "${field.question}":`, error);
              dateValue = null;
            }
          }
        }
        
        return {
          field: field.uid,
          question: field.question,
          description: field.description ?? null,
          value: {
            value: dateValue ? [dateValue] : [],
            files: []
          },
          inputComponent: field.inputComponent,
          children: []
        };
      }

      // Handle CHECKBOX, CAMERA, and ESIGN components
      if (field.inputComponent === InputComponents.CHECKBOX) {
        return {
          field: field.uid,
          question: field.question,
          description: field.description ?? null,
          value: {
            value: Array.isArray(fieldValue) ? fieldValue : [],
            files: []
          },
          inputComponent: field.inputComponent,
          children: []
        };
      }

      if (field.inputComponent === InputComponents.CAMERA && field.settings?.cameraMode=="IMAGE") {

        return {
          field: field.uid,
          question: field.question,
          description: field.description ?? null,
          value: {
            value: '',
            files: Array.isArray(fieldValue) ? fieldValue : []
          },
          inputComponent: field.inputComponent,
          children: []
        };
      }

      if (field.inputComponent === InputComponents.ESIGN) {
         return {
          field: field.uid,
          question: field.question,
          description: field.description ?? null,
          value: {
            value: '',
            files: Array.isArray(fieldValue) ? fieldValue : []
          },
          inputComponent: field.inputComponent,
          children: []
        };
      }

      // Handle SELECT and RADIO - save as string, not array
      if (field.inputComponent === InputComponents.SELECT || field.inputComponent === InputComponents.RADIO) {
        return {
          field: field.uid,
          question: field.question,
          description: field.description ?? null,
          value: {
            value: fieldValue || '',
            files: []
          },
          inputComponent: field.inputComponent,
          children: []
        };
      }

      return {
        field: field.uid,
        question: field.question,
        description: field.description ?? null,
        value: {
          value: fieldValue,
          files: []
        },
        inputComponent: field.inputComponent,
        children: []
      };
    });
  };

  const hypertext = (key: string, touched: any, errors: any, submitCount: number) => {
    return touched[key] || submitCount > 0 ? errors[key] : '';
  };

  const renderInputField = (
    field: Field,
    index: number,
    values: Record<string, any>,
    handleChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>,
    setFieldValue: (field: string, value: any) => void,
    errors: Record<string, any>,
    touched: Record<string, any>,
    submitCount: number
  ) => {
    const keys = field.question;
    const errorMessages = touched[keys] && errors[keys] ? errors[keys] : '';
    switch (field.inputComponent) {
      case InputComponents.TEXT:
        if (field.settings?.multiple) {
          // Ensure value is always an array
          const arr = Array.isArray(values[keys]) ? values[keys] : values[keys] ? [values[keys]] : [''];
          return (
            <Box>
              {arr.map((val: string, idx: number) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    type="text"
                    fullWidth
                    variant="outlined"
                    name={`${keys}[${idx}]`}
                    value={val}
                    sx={{
                      '& .MuiInputBase-input': {
                        padding: '8px',
                      }
                    }}
                    error={Boolean(errorMessages)}
                    onChange={(e) => {
                      const newArr = [...arr];
                      newArr[idx] = e.target.value;
                      setFieldValue(keys, newArr);
                    }}
                    multiline={field.settings?.isMultiline}
                    rows={field.settings?.rows || (field.settings?.isMultiline ? 3 : 1)}
                  />
                  {arr.length > 1 && idx !== arr.length - 1 && (
                    <IconButton
                      sx={{ ml: 1 }}
                      color="error"
                      onClick={() => {
                        const newArr = arr.filter((_val: any, i: number) => i !== idx);
                        setFieldValue(keys, newArr);
                      }}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
                  {idx === arr.length - 1 && (
                    <IconButton
                      sx={{ ml: 1 }}
                      color="primary"
                      onClick={() => {
                        setFieldValue(keys, [...arr, '']);
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          );
        }
        return (
          <TextField
            type="text"
            fullWidth
            variant="outlined"
            sx={{
              '& .MuiInputBase-input': {
                padding: '8px',
              }
            }}
            name={keys}
            value={values[keys] || ''}
            onChange={(e) => setFieldValue(keys, e.target.value)}
            multiline={!!field.settings?.isMultiline}
            rows={field.settings?.rows || (field.settings?.isMultiline ? 3 : 1)}
            error={Boolean((touched[keys] || submitCount > 0) && errors[keys])}
            helperText={hypertext(keys, touched, errors, submitCount)}
            InputProps={
              field.hasAddIcon
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton>
                          <AddIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                : undefined
            }
          />
        );

      case InputComponents.NUMBER:
        return (
          <TextField
            type="number"
            fullWidth
            variant="outlined"
            name={keys}
            sx={{
                '& .MuiInputBase-input': {
                 padding: '8px',
              }
            }}
            value={values[keys] || ''}
            onChange={handleChange}
            error={Boolean(touched[keys] && errors[keys])}
            helperText={hypertext(keys, touched, errors, submitCount)}
          />
        );

      case InputComponents.PASSWORD:
        return (
          <TextField
            type={passwordVisibility[keys] ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            name={keys}
            value={values[keys] || ''}
            onChange={(e) => setFieldValue(keys, e.target.value)}
            error={Boolean(touched[keys] && errors[keys])}
            helperText={hypertext(keys, touched, errors, submitCount)}
            sx={{
              '& .MuiInputBase-input': {
                padding: '8px', 
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleTogglePassword(keys)} edge="end">
                    {passwordVisibility[keys] ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        );

      case InputComponents.FILE:
        return (
          <>
            <MultiFileUploader
              value={values[keys]}
              onChange={(files) => setFieldValue(keys, files)}
              maxSize={15}
              entityId={stepItem?.uid}
              entityType="authkey"
            />
            {Array.isArray(values[keys]) && values[keys].length > 0 && (
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Uploaded file{values[keys].length > 1 ? 's' : ''}:
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {values[keys].map((file: any, idx: number) => {
                    const fileName = file.file_name || file.name || 'Unknown file';
                    const fileType = file.mime_type || file.type || '';
                    const isImage = fileType.startsWith('image/');
                    const fileUrl = file.url;

                    return (
                      <Box
                        key={idx}
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: isImage ? 'transparent' : 'grey.50',
                          position: 'relative',
                          padding: '8px'
                        }}
                        title={fileName}
                      >
                        {isImage && fileUrl ? (
                          <img
                            src={fileUrl}
                            alt={fileName}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 0.5,
                              textAlign: 'center'
                            }}
                          >
                            <InsertDriveFileIcon
                              sx={{
                                fontSize: 24,
                                color: 'primary.main',
                                mb: 0.5
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: '8px',
                                color: 'text.secondary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                                fontWeight: 500
                              }}
                            >
                              {fileType ? fileType.split('/')[1]?.substring(0, 3).toUpperCase() || 'FILE' : 'FILE'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
            {(touched[keys] || submitCount > 0) && errors[keys] && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {errors[keys]}
              </Typography>
            )}
          </>
        );

      case InputComponents.DATE:
        const isTimeRequired = field.settings?.isTimeRequired;
        const includeTimezone = field.settings?.includeTimezone !== false; // default to true
        
        return (
          <DateTimePickerWithTimezone
            value={values[keys]}
            onChange={(newValue: any) => {
              setFieldValue(keys, newValue);
            }}
            includeTime={isTimeRequired}
            showTimezone={includeTimezone}
            error={Boolean((touched[keys] || submitCount > 0) && errors[keys])}
            helperText={touched[keys] || submitCount > 0 ? errors[keys] : ''}
            fullWidth={true}
            required={field.settings?.required}
          />
        );

      case InputComponents.SELECT:
        return (
          <TextField
            fullWidth
            select
            variant="outlined"
            name={keys}
            value={values[keys] || ''}
            onChange={handleChange}
            error={Boolean((touched[keys] || submitCount > 0) && errors[keys])}
            helperText={touched[keys] || submitCount > 0 ? errors[keys] : ''}
            sx={{
              '& .MuiInputBase-input': {
                padding: '8px'
              }
            }}
          >
            <MenuItem value="">Please select</MenuItem>
            {field.options?.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        );

      case InputComponents.CHECKBOX:
        return (
          <Box>
            <FormGroup>
              {field.options?.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={Array.isArray(values[keys]) && values[keys].includes(option)}
                      onChange={(e) => {
                        const currentValues = Array.isArray(values[keys]) ? values[keys] : [];
                        if (e.target.checked) {
                          setFieldValue(keys, [...currentValues, option]);
                        } else {
                          setFieldValue(keys, currentValues.filter((val: string) => val !== option));
                        }
                      }}
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
            {(touched[keys] || submitCount > 0) && errors[keys] && (
              <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                {errors[keys]}
              </Typography>
            )}
          </Box>
        );

      case InputComponents.RADIO:
        return (
          <Box>
            <RadioGroup row name={keys} value={values[keys]} onChange={handleChange}>
              {field.options?.map((opt) => (
                <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} />
              ))}
            </RadioGroup>
            {(touched[keys] || submitCount > 0) && errors[keys] && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {errors[keys]}
              </Typography>
            )}
          </Box>
        );

      case InputComponents.CAMERA:
       if (field.settings?.cameraMode === "IMAGE") {
           return (
              <>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setCameraOpen(true);
                    setCameraField(keys);
                  }}
                >
                  Open Camera
                </Button>

                <Box mt={2} display="flex" gap={2} flexWrap="wrap">
                  {Array.isArray(values[keys]) &&
                    values[keys].map((img: any, idx: number) => (
                      <Box key={idx} position="relative">
                        {typeof img === 'string' ? (
                          <img src={img} alt={`snap-${idx}`} width={100} height={100} style={{ objectFit: 'cover', borderRadius: 4 }} />
                        ) : img instanceof File || img instanceof Blob ? (
                          <img
                            src={URL.createObjectURL(img)}
                            alt={`snap-${idx}`}
                            width={100}
                            height={100}
                            style={{ objectFit: 'cover', borderRadius: 4 }}
                          />
                        ) : null}
                        <IconButton
                          size="small"
                          onClick={() => {
                            const updated = values[keys].filter((_: any, i: number) => i !== idx);
                            setFieldValue(keys, updated);
                          }}
                          sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                </Box>
                {(touched[keys] || submitCount > 0) && errors[keys] && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {errors[keys]}
                  </Typography>
                )}

                <Dialog open={cameraOpen && cameraField === keys} onClose={() => setCameraOpen(false)} fullWidth maxWidth="sm">
                  <DialogTitle>Capture Photo</DialogTitle>
                  <DialogContent>
                    <Box display="flex" justifyContent="center" mb={2}>
                      <Camera
                        ref={cameraRef}
                        aspectRatio={16 / 9}
                        errorMessages={{
                          noCameraAccessible: 'No camera detected.',
                          permissionDenied: 'Camera access was denied.',
                          switchCamera: 'Unable to switch camera.',
                          canvas: 'Canvas not supported in this browser.'
                        }}
                      />
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setCameraOpen(false)}>Cancel</Button>
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (cameraRef.current) {
                          const photo = cameraRef.current.takePhoto();
                          const existing = values[cameraField] || [];
                          setFieldValue(cameraField, [...existing, photo]);
                          setCameraOpen(false);
                        }
                      }}
                    >
                      Capture
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            );
          } else {
            // Video mode
            return (
              <>
                <MultiFileUploader
                  value={values[keys]}
                  onChange={(files) => setFieldValue(keys, files)}
                  maxSize={50}
                  entityId={stepItem?.uid}
                  entityType="video"
                  // accept="video/*"
                />
                {Array.isArray(values[keys]) && values[keys].length > 0 && (
                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Uploaded video{values[keys].length > 1 ? 's' : ''}:
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {values[keys].map((file: any, idx: number) => (
                        <Box
                          key={idx}
                          sx={{
                            width: 120,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            p: 1
                          }}
                        >
                          <video
                            width="100%"
                            controls
                            src={file.url || URL.createObjectURL(file)}
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              const updated = values[keys].filter((_: any, i: number) => i !== idx);
                              setFieldValue(keys, updated);
                            }}
                            sx={{ mt: 1 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                {(touched[keys] || submitCount > 0) && errors[keys] && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {errors[keys]}
                  </Typography>
                )}
              </>
            );
          }

      case InputComponents.ESIGN:
        return (
          <>
            <MultiFileUploader
              value={values[keys]}
              onChange={(files) => setFieldValue(keys, files)}
              maxSize={3}
              entityId={stepItem?.uid}
              entityType="authkey"
            />
            {Array.isArray(values[keys]) && values[keys].length > 0 && (
              <Box mt={2}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Uploaded file{values[keys].length > 1 ? 's' : ''}:
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {values[keys].map((file: any, idx: number) => {
                    const fileName = file.file_name || file.name || 'Unknown file';
                    const fileType = file.mime_type || file.type || '';
                    const isImage = fileType.startsWith('image/');
                    const fileUrl = file.url;

                    return (
                      <Box
                        key={idx}
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: isImage ? 'transparent' : 'grey.50',
                          position: 'relative',
                          padding: '8px'
                        }}
                        title={fileName}
                      >
                        {isImage && fileUrl ? (
                          <img
                            src={fileUrl}
                            alt={fileName}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 0.5,
                              textAlign: 'center'
                            }}
                          >
                            <InsertDriveFileIcon
                              sx={{
                                fontSize: 24,
                                color: 'primary.main',
                                mb: 0.5
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: '8px',
                                color: 'text.secondary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                                fontWeight: 500
                              }}
                            >
                              {fileType ? fileType.split('/')[1]?.substring(0, 3).toUpperCase() || 'FILE' : 'FILE'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
            {(touched[keys] || submitCount > 0) && errors[keys] && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {errors[keys]}
              </Typography>
            )}
          </>
        );
      
      
      case InputComponents.SWITCH:
        return (
          <Box>
            <Typography variant="body1">{field.settings?.label}</Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <IOSSwitch
                checked={Boolean(values[keys]?.value)}
                onChange={(e) => {
                  setFieldValue(keys, { value: e.target.checked });
                }}
              />
            </Box>
            {(touched[keys] as any)?.files && (errors[keys] as any)?.files && (
              <Typography variant="caption" color="error">
                {Array.isArray((errors[keys] as any)?.files)
                  ? (errors[keys] as any)?.files[0]?.file || `${field.question} is required`
                  : (errors[keys] as any)?.files}
              </Typography>
            )}
          </Box>
        );

      case InputComponents.RESOURCE:
        return (
          <>
            <ResourceSelector
              field={field}
              value={values[keys]}
              onChange={(value) => setFieldValue(keys, value)}
              resourceType={field.resourceType || field.settings?.resourceType}
              resourceList={(field as any).resourceList}
              multiple={field.settings?.multiple}
            />
            {(touched[keys] || submitCount > 0) && errors[keys] && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {typeof errors[keys] === 'object'
                  ? (errors[keys] as any)?.id || (errors[keys] as any)?.resourceType || 'Resource is required'
                  : (errors[keys] as string)}
              </Typography>
            )}
          </>

        );

      default:
        return (
          <TextField
            fullWidth
            variant="outlined"
            name={keys}
            value={values[keys] || ''}
            onChange={handleChange}
            error={Boolean((touched[keys] || submitCount > 0) && errors[keys])}
            helperText={touched[keys] || submitCount > 0 ? errors[keys] : ''}
            sx={{
              '& .MuiInputBase-input': {
                padding: '8px'
              }
            }}
          />
        );
    }
  };

  // Upload CAMERA images to S3 (like the native app) so edits store file refs,
  // not base64. Existing refs / http URLs pass through unchanged.
  const uploadCameraFields = async (vals: Record<string, any>): Promise<Record<string, any>> => {
    const out = { ...vals };
    for (const field of schemaFields) {
      if (field.inputComponent !== InputComponents.CAMERA) continue;
      const imgs = Array.isArray(vals[field.question]) ? vals[field.question] : [];
      const refs: any[] = [];
      for (let i = 0; i < imgs.length; i++) {
        const img = imgs[i];
        if (img && typeof img === 'object' && (img.file_path || img.public_url)) { refs.push(img); continue; }
        let file: File | null = null;
        if (img instanceof File) file = img;
        else if (img instanceof Blob) file = new File([img], `capture-${Date.now()}-${i}.jpg`, { type: (img as Blob).type || 'image/jpeg' });
        else if (typeof img === 'string' && img.startsWith('data:')) {
          const blob = await (await fetch(img)).blob();
          file = new File([blob], `capture-${Date.now()}-${i}.jpg`, { type: blob.type || 'image/jpeg' });
        } else if (typeof img === 'string' && img.startsWith('http')) { refs.push({ public_url: img, file_path: img }); continue; }
        if (!file) continue;
        const res: any = await uploadAsset(file, 'transaction/edit-event');
        const f = res?.data ?? res;
        if (f?.file_path || f?.public_url) refs.push({ file_name: f.file_name, file_path: f.file_path, mime_type: f.mime_type, public_url: f.public_url });
      }
      out[field.question] = refs;
    }
    return out;
  };

  const handleSubmit = async (values: Record<string, any>, { setSubmitting }: any) => {
    try {
      const uploadedValues = await uploadCameraFields(values);
      const transformedData = transformFormDataForSave(uploadedValues);
      const stepId = stepItem._id || stepItem.id;
      
      if (!stepId) {
        console.error('No valid step ID found');
        return;
      }
      
      await onSave(stepId, transformedData);
    } catch (error) {
      console.error('Error saving form:', error);
    } finally {
      setSubmitting(false);
    }
  };

   function toSentenceCase(input: string): string {
      const quotedMap: Record<number, string> = {};
      let idx = 0;

      // Step 1: Replace "quoted" text with lowercase placeholders
      //         Store the UPPERCASED version in quotedMap
      const withPlaceholders = input.replace(
        /"([^"]*)"/g,
        (_: string, inner: string): string => {
          const key = `__q${idx}__`;
          quotedMap[idx] = inner.toUpperCase();
          idx++;
          return key;
        }
      );

      // Step 2: Lowercase everything, then capitalize only the first char
      const sentenceCased = withPlaceholders
        .toLowerCase()
        .replace(/^\w/, (c: string): string => c.toUpperCase());

      // Step 3: Restore placeholders with their UPPERCASE values
      return sentenceCased.replace(
        /__q(\d+)__/g,
        (_: string, i: string): string => quotedMap[Number(i)]
      );
    }
  

  return (
    <Paper
      elevation={0}
      sx={{
        background: 'white',
        borderRadius: "10px",
        marginBottom: "10px",
        border: '1px solid',
        borderColor: 'grey.200',
        transition: 'all 0.2s ease-in-out',
        overflow: 'hidden'
      }}
    >
      {/* Header Section - matching details page style 0*/}
      <Box
        sx={{
          border: 'none',
          outline: 'none',
          color: '#fff',
          px: 1,
          py: 1,
          background: theme.palette.primary.dark,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            fontWeight={600}
            variant="h6"
            sx={{
              color: '#fff', 
              fontSize: '12px',
              letterSpacing: '0.025em',
              textTransform: 'uppercase'
            }}
          >
            Editing: {stepItem.step?.title || 'Transaction Step'}
          </Typography>
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{ p: 1 }}>
        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, handleSubmit, setFieldValue, isSubmitting, touched, errors, submitCount }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={1}>
                {schemaFields.map((field, index) => (
                  <Grid 
                    item 
                    xs={12} 
                    md={
                      field.inputComponent === InputComponents.TEXT && field.settings?.isMultiline ? 12 :
                      field.inputComponent === InputComponents.CAMERA ? 12 :
                      field.inputComponent === InputComponents.FILE ? 12 :
                      field.inputComponent === InputComponents.ESIGN ? 12 :
                      6
                    } 
                    key={field.question}
                  >
                    <Box >
                      <Typography 
                        fontWeight={600}
                        variant="body2"
                        sx={{
                          color: 'text.primary',
                          mb: 0.5,
                          fontSize: '0.8rem',
                          letterSpacing: '0.025em'
                        }}
                      >
                        {toSentenceCase(field.question)}
                        {field.settings?.required && (
                          <Typography component="span" color="error">
                            *
                          </Typography>
                        )}
                      </Typography>
                      {renderInputField(
                        field,
                        index,
                        values,
                        handleChange,
                        setFieldValue,
                        errors,
                        touched,
                        submitCount
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Footer Section with buttons - matching details page style */}
              <Box
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'grey.200',
                  mt: 1,
                  pt: 1,
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'flex-end'
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={onCancel}
                  disabled={isSubmitting || saving}
                  sx={{
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: 'grey.400',
                      bgcolor: 'grey.50'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={isSubmitting || saving}
                  sx={{
                    bgcolor: '#132D76',
                    '&:hover': {
                      bgcolor: '#0f2458'
                    }
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Paper>
  );
};

export default EditForm;